import React, { useState, useEffect, useRef } from 'react';
import { ArrowLeft, ExternalLink, ChevronLeft, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import '../styles/booking-embed.css';

// Media items for the carousel - easily swap these out with your own images/videos
// Mixed gallery: event images including iFLY, paddleboarding, aerial, and more
const galleryMedia = [
  { type: 'image', src: 'https://customer-assets.emergentagent.com/job_57c0eb9c-3961-4c7d-95b0-bd77dac9c429/artifacts/94fz3cdk_Cal_pouring%20bubbly.jpg', alt: 'Aerial champagne pour' },
  { type: 'image', src: 'https://customer-assets.emergentagent.com/job_57c0eb9c-3961-4c7d-95b0-bd77dac9c429/artifacts/a2od9sbz_Ayla_iFLY_Flying1500mb.png', alt: 'Kid flying at iFLY' },
  { type: 'image', src: 'https://customer-assets.emergentagent.com/job_57c0eb9c-3961-4c7d-95b0-bd77dac9c429/artifacts/xh6zoq6x_BirdeBirthday.jpeg', alt: 'Birthday party event' },
  { type: 'image', src: 'https://customer-assets.emergentagent.com/job_57c0eb9c-3961-4c7d-95b0-bd77dac9c429/artifacts/dosxiw2t_JennaAFTrap1500mb.png', alt: 'Jenna on trapeze at studio' },
  { type: 'image', src: 'https://customer-assets.emergentagent.com/job_57c0eb9c-3961-4c7d-95b0-bd77dac9c429/artifacts/ognwvpdb_Group_PaddleBoard_Sit1500mb.png', alt: 'Kids paddleboarding on water' },
  { type: 'image', src: 'https://customer-assets.emergentagent.com/job_57c0eb9c-3961-4c7d-95b0-bd77dac9c429/artifacts/pwd8324e_cal%20layback%20in%20lollipop.JPG', alt: 'Aerial performance' },
  { type: 'image', src: 'https://customer-assets.emergentagent.com/job_57c0eb9c-3961-4c7d-95b0-bd77dac9c429/artifacts/9prg9hr6_Group_iFLY_STEMO21500mb.png', alt: 'Kids at iFLY watching colorful balls' },
  { type: 'image', src: 'https://customer-assets.emergentagent.com/job_57c0eb9c-3961-4c7d-95b0-bd77dac9c429/artifacts/3e7z5sy6_WinniePicnic.JPG', alt: 'Event setup' },
  { type: 'image', src: 'https://customer-assets.emergentagent.com/job_57c0eb9c-3961-4c7d-95b0-bd77dac9c429/artifacts/owjot5lu_Group_iFLY_Tunnel1500mb.png', alt: 'Kids group at iFLY wind tunnel' },
  { type: 'image', src: 'https://customer-assets.emergentagent.com/job_57c0eb9c-3961-4c7d-95b0-bd77dac9c429/artifacts/5ll3grvg_Group_PB_ShoreStanding1500mb.png', alt: 'Kids with paddles on shore' },
];

const BOOKING_OPTIONS = [
  {
    id: 'private',
    label: 'Private Parties & Events',
    note: null,
    hidden: true, // temporarily hidden per owner request
    src: 'https://app.acuityscheduling.com/schedule.php?owner=37343334&appointmentType=95283590',
    title: 'Book Private Event'
  },
  {
    id: 'trapeze',
    label: 'Adult Trapeze Pop-up',
    note: 'Oct 23 · 6:30pm',
    expires: '2026-10-23T18:30:00',
    src: 'https://app.acuityscheduling.com/schedule.php?owner=37343334&appointmentType=99139055',
    title: 'Book Adult Trapeze Pop-up'
  },
  {
    id: 'glow',
    label: '🎃 Bungee HALLOWEEN Glow Party',
    note: 'Oct 30 · 5:30 & 7:00pm',
    expires: '2026-10-30T19:00:00',
    src: 'https://app.acuityscheduling.com/schedule.php?owner=37343334&appointmentType=99139371',
    title: 'Book Bungee Halloween Glow Party'
  },
];

const now = new Date();
const liveOptions = BOOKING_OPTIONS.filter(
  (o) => !o.hidden && (!o.expires || now < new Date(o.expires))
);
// If every event has passed, fall back to the private-events booking
const VISIBLE_OPTIONS = liveOptions.length > 0
  ? liveOptions
  : BOOKING_OPTIONS.filter((o) => o.id === 'private');

const BookEventPage = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(VISIBLE_OPTIONS[0]);
  const videoRef = useRef(null);
  const timerRef = useRef(null);

  const IMAGE_DURATION = 4000; // 4 seconds for images
  const VIDEO_BUFFER = 500;

  const goToNext = () => {
    setIsTransitioning(true);
    setTimeout(() => {
      setCurrentIndex((prev) => (prev + 1) % galleryMedia.length);
      setIsTransitioning(false);
    }, 300);
  };

  const goToPrev = () => {
    setIsTransitioning(true);
    setTimeout(() => {
      setCurrentIndex((prev) => (prev - 1 + galleryMedia.length) % galleryMedia.length);
      setIsTransitioning(false);
    }, 300);
  };

  // Handle auto-advancement
  useEffect(() => {
    const currentMedia = galleryMedia[currentIndex];

    if (currentMedia.type === 'image') {
      timerRef.current = setTimeout(goToNext, IMAGE_DURATION);
    }

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [currentIndex]);

  // Handle video end
  const handleVideoEnd = () => {
    setTimeout(goToNext, VIDEO_BUFFER);
  };

  // Reset and play video when it becomes active
  useEffect(() => {
    if (galleryMedia[currentIndex].type === 'video' && videoRef.current) {
      videoRef.current.currentTime = 0;
      videoRef.current.play().catch(() => {
        setTimeout(goToNext, 1000);
      });
    }
  }, [currentIndex]);

  // Load Acuity embed script
  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://embed.acuityscheduling.com/js/embed.js';
    script.type = 'text/javascript';
    script.async = true;
    document.body.appendChild(script);

    return () => {
      const existingScript = document.querySelector('script[src="https://embed.acuityscheduling.com/js/embed.js"]');
      if (existingScript) {
        existingScript.remove();
      }
    };
  }, []);

  // Ken Burns effect variations
  const kbEffects = ['kb-zoom-in', 'kb-zoom-out', 'kb-pan-left', 'kb-pan-right'];
  const getKbEffect = (index) => kbEffects[index % kbEffects.length];

  const currentMedia = galleryMedia[currentIndex];

  return (
    <div className="booking-embed-page with-gallery">
      {/* Mini Hero Gallery Carousel */}
      <div className="booking-gallery" data-testid="event-gallery">
        <div className={`gallery-media ${isTransitioning ? 'transitioning' : ''}`}>
          {currentMedia.type === 'video' ? (
            <video
              ref={videoRef}
              key={currentMedia.src}
              className="gallery-video"
              src={currentMedia.src}
              muted
              playsInline
              onEnded={handleVideoEnd}
            />
          ) : (
            <div
              key={currentMedia.src}
              className={`gallery-image ${getKbEffect(currentIndex)}`}
              style={{ backgroundImage: `url(${currentMedia.src})` }}
            />
          )}
        </div>

        {/* Gallery overlay with title */}
        <div className="gallery-overlay">
          <h1 className="gallery-title">Book Your Event Here</h1>
          <p className="gallery-subtitle">Private parties, kid's parties, and unforgettable experiences</p>
        </div>

        {/* Navigation arrows */}
        {galleryMedia.length > 1 && (
          <>
            <button 
              className="gallery-nav-btn prev" 
              onClick={(e) => { e.stopPropagation(); goToPrev(); }}
              aria-label="Previous"
            >
              <ChevronLeft size={24} />
            </button>
            <button 
              className="gallery-nav-btn next" 
              onClick={(e) => { e.stopPropagation(); goToNext(); }}
              aria-label="Next"
            >
              <ChevronRight size={24} />
            </button>
          </>
        )}

        {/* Progress indicators */}
        <div className="gallery-indicators">
          {galleryMedia.map((_, index) => (
            <button
              key={index}
              className={`gallery-indicator ${index === currentIndex ? 'active' : ''}`}
              onClick={() => {
                if (index !== currentIndex) {
                  setIsTransitioning(true);
                  setTimeout(() => {
                    setCurrentIndex(index);
                    setIsTransitioning(false);
                  }, 300);
                }
              }}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      </div>

      <div className="booking-embed-header compact">
        <Link to="/events" className="back-link">
          <ArrowLeft size={20} />
          Back to Schedule
        </Link>
      </div>

      {/* Event selector */}
      <div className="event-picker" data-testid="event-picker">
        {VISIBLE_OPTIONS.map((opt) => (
          <button
            key={opt.id}
            className={`event-picker-btn ${selectedBooking.id === opt.id ? 'active' : ''} ${opt.id === 'glow' ? 'glow' : ''}`}
            onClick={() => setSelectedBooking(opt)}
            data-testid={`event-picker-${opt.id}`}
          >
            <span className="event-picker-label">{opt.label}</span>
            {opt.note && <span className="event-picker-note">{opt.note}</span>}
          </button>
        ))}
      </div>

      <div className="booking-embed-container" data-testid="acuity-embed-container">
        <iframe 
          key={selectedBooking.id}
          src={`${selectedBooking.src}&ref=embedded_csp`} 
          title={selectedBooking.title} 
          width="100%" 
          height="800" 
          frameBorder="0" 
          allow="payment"
          className="booking-embed-iframe acuity-iframe"
        />
      </div>

      <div className="booking-embed-fallback">
        <p>Having trouble?</p>
        <a 
          href={selectedBooking.src} 
          target="_blank" 
          rel="noopener noreferrer"
          className="fallback-link"
        >
          Open booking page in a new tab
          <ExternalLink size={16} />
        </a>
      </div>
    </div>
  );
};

export default BookEventPage;
